'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Cetaphil Have Microplastics? All 79 Published Formulas Checked (2026)"
      author="MicroPlastics Research Desk"
      date="August 9, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/does-cetaphil-have-microplastics.webp"
      heroImageAlt="Unbranded sage-accent gentle cleanser, lotion and moisturizer on a bathroom sink"
    >
      <BlogLastUpdated iso="2026-08-09" formatted="August 9, 2026" />

      <BlogTLDR>
        <strong>Five of 79 Cetaphil formulas contain a solid synthetic polymer
        particle — and three of the five are SPF moisturisers.</strong> That is
        about 6%, statistically level with{' '}
        <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe</Link>{' '}
        and far below the makeup brands we audited in July. The named products
        are the <strong>Daily Facial Moisturizer SPF 15</strong> and{' '}
        <strong>DermaControl Oil Absorbing Moisturizer SPF 30</strong> (both
        polymethyl methacrylate), the{' '}
        <strong>Healthy Radiance Whipped Day Cream SPF 30</strong> (acrylates
        copolymer), the <strong>Hydrating Eye Gel-Cream</strong> and the{' '}
        <strong>Fast Rescue Pimple Patch</strong>. The classic Gentle Skin
        Cleanser and the Moisturizing Lotion — the two products the brand is
        actually known for — are <strong>clean</strong>. And the entire baby
        range is clean too, once you correctly exclude a water-soluble polymer
        that most ingredient checkers wrongly flag.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>5 of 79</strong> published Cetaphil formulas contain a solid polymer particle — around 6%.</>,
          <><strong>Three of the five are sunscreens or SPF moisturisers.</strong> This is a category pattern, not a brand one.</>,
          <>The <strong>Gentle Skin Cleanser and Moisturizing Lotion are clean</strong>, as is the Daily Facial Cleanser.</>,
          <>The <strong>Cetaphil Baby range is clean</strong> of solid particles. Six products contain polyquaternium, which is water-soluble and not a microplastic.</>,
          <>The <strong>Fast Rescue Pimple Patch</strong> is acrylates copolymer — but that is the patch itself, which you peel off and bin.</>,
          <>Cetaphil is owned by <strong>Galderma</strong>, a pharmaceutical company, and publishes full INCI lists you can audit.</>,
          <>Level with CeraVe overall, but the hits are <strong>more concentrated</strong> — fix your SPF and you have fixed almost all of it.</>,
        ]}
      />

      <KeyStats
        heading="The Cetaphil audit"
        stats={[
          { value: '79', label: 'formulas with published INCI', context: 'pulled from cetaphil.com product pages in August 2026', source: 'MicroPlastics Research Desk audit' },
          { value: '5', label: 'contain a solid polymer particle', context: 'about 6%, level with CeraVe’s 4 of 71', source: 'MicroPlastics Research Desk audit' },
          { value: '3 of 5', label: 'are SPF products', context: 'sunscreen texture is where polymer spheres cluster across every brand', source: 'MicroPlastics Research Desk audit' },
          { value: '0', label: 'in the Cetaphil Baby range', context: 'once water-soluble polyquaternium is correctly excluded', source: 'cetaphil.com ingredient listings', sourceUrl: 'https://www.cetaphil.com/' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        If you use Cetaphil the way most people use Cetaphil — the Gentle Skin
        Cleanser and the Moisturizing Lotion —{' '}
        <strong>you have nothing to change</strong>. Neither contains a solid
        synthetic polymer particle.
      </p>
      <p>
        The five products that do are, with one exception, sun protection. That
        is a genuinely useful finding because it is{' '}
        <strong>actionable in one move</strong>: if you swap a single product
        category, you have addressed nearly everything this audit found.
      </p>

      <InlineGuessReveal
        source="cetaphil_spf_cluster"
        question="Three of the five Cetaphil products with microplastics are the same kind of product. Which?"
        options={[
          { label: 'Cleansers' },
          { label: 'Baby products' },
          { label: 'SPF moisturisers', correct: true },
          { label: 'Body lotions' },
        ]}
        answer="SPF moisturisers"
        answerNote={
          <>
            The Daily Facial Moisturizer SPF 15, DermaControl SPF 30 and Healthy
            Radiance Whipped Day Cream SPF 30. Sunscreen has to protect and also
            not feel like sunscreen, and polymer microspheres are the standard
            way to deliver that second promise. The same pattern shows up in
            CeraVe and Neutrogena.
          </>
        }
        ctaText="Sunscreen is where these hide. Scan the one in your bag."
        ctaLabel="Scan your SPF"
      />

      <RiskQuizInlineNudge source="blog_inline_cetaphil" />

      <h2>The five</h2>

      <BlogComparisonTable
        caption="Every Cetaphil formula containing a solid synthetic polymer particle, from Cetaphil's own published ingredient lists (checked August 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Polymer' },
          { key: 'job', label: 'What it does' },
          { key: 'priority', label: 'Priority' },
        ]}
        rows={[
          { product: 'Daily Facial Moisturizer SPF 15', polymer: 'Polymethyl methacrylate (PMMA)', job: 'Soft-focus spheres that blur texture and cut sunscreen shine', priority: 'Highest — daily leave-on' },
          { product: 'DermaControl Oil Absorbing Moisturizer SPF 30', polymer: 'Polymethyl methacrylate (PMMA)', job: 'Absorbs oil and mattifies; the "oil absorbing" claim is largely this', priority: 'Highest — daily leave-on' },
          { product: 'Healthy Radiance Whipped Day Cream SPF 30', polymer: 'Acrylates copolymer, styrene/acrylates copolymer', job: 'Film former and opacifier for the whipped texture and glow', priority: 'High — daily leave-on' },
          { product: 'Hydrating Eye Gel-Cream', polymer: 'HDI/trimethylol hexyllactone crosspolymer', job: 'Elastomer for slip and a smooth under-eye finish', priority: 'Moderate — small quantity, leave-on' },
          { product: 'Fast Rescue Pimple Patch', polymer: 'Acrylates copolymer', job: 'The adhesive hydrocolloid patch itself', priority: 'Low — see below' },
        ]}
      />

      <h2>The pimple patch is a different case</h2>
      <p>
        The Fast Rescue Pimple Patch technically belongs on that list, and it is
        also the least concerning item on it. The acrylates copolymer{' '}
        <strong>is the patch</strong> — the adhesive hydrocolloid disc you stick
        on and peel off a few hours later.
      </p>
      <p>
        That is a meaningfully different exposure from a cream you rub in and
        leave. It stays as an intact object, it does not disperse into the
        formula, and it goes in the bin rather than down the drain. It is a
        piece of plastic you deliberately apply and deliberately remove.{' '}
        <strong>We would not change your behaviour over this one</strong>, and
        we flag it only because leaving it out would make the audit
        inconsistent.
      </p>

      <h2>The baby range, and why most checkers get it wrong</h2>
      <p>
        Six Cetaphil products — including the Gentle Baby Wash and the Tear Free
        Baby Wash and Shampoo — contain{' '}
        <strong>polyquaternium</strong>. Run those through most ingredient-checking
        apps and you will be told your baby wash contains microplastics.
      </p>
      <p>
        It does not. Polyquaternium is a{' '}
        <strong>water-soluble cationic polymer</strong> used as a conditioning
        agent. It is synthetic, it is a polymer, and it is dissolved in the
        bottle rather than suspended as solid particles — which puts it outside
        the definition of synthetic polymer microparticles that the EU
        restriction actually targets.
      </p>
      <p>
        We excluded it from our counts across all three brands in this cluster.
        Including it would have added 22 products and produced a much scarier
        headline that would not survive contact with the regulation. If you have
        been worrying about a Cetaphil baby wash because an app flagged it,{' '}
        <strong>you can stop.</strong>
      </p>

      <BlogAppCTA
        headline="Check what else is in your routine"
        sub="Scan any cleanser, SPF or serum with MicroPlastics for its polymers, a 0–100 risk score, and a cleaner product in the same category."
      />

      <h2>Cetaphil versus CeraVe</h2>
      <p>
        These two get compared constantly, and on this measure they are{' '}
        <strong>effectively tied</strong>: 5 of 79 against 4 of 71. Around 6%
        either way. Anyone telling you one is dramatically cleaner than the other
        is reading noise.
      </p>
      <p>
        The difference is <strong>distribution</strong>. Cetaphil&apos;s hits
        cluster in SPF, so one category swap fixes almost all of it.
        CeraVe&apos;s are spread across four unrelated products — a balm, a body
        wash, a serum and an SPF lotion — so there is no single move. If you want
        the tidier fix, Cetaphil&apos;s problem is more concentrated. Both are
        good outcomes.
      </p>

      <BlogComparisonTable
        caption="The three drugstore brands side by side, from 290 published ingredient lists (August 2026)"
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'result', label: 'Solid polymer particles' },
          { key: 'pattern', label: 'Pattern' },
        ]}
        rows={[
          { brand: 'CeraVe', result: '4 of 71 (6%)', pattern: 'Scattered across four unrelated products' },
          { brand: 'Cetaphil', result: '5 of 79 (6%)', pattern: 'Concentrated in SPF moisturisers' },
          { brand: 'Neutrogena', result: '38 of 140 (27%)', pattern: 'Hydro Boost line plus its makeup range' },
        ]}
      />

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Keep the Gentle Skin Cleanser and Moisturizing Lotion.</strong>{' '}
          Both clean. These are the products most Cetaphil users own.
        </li>
        <li>
          <strong>Swap the SPF if you want one change that covers it.</strong>{' '}
          Three of five hits are SPF moisturisers; mineral sunscreens generally
          skip these polymers.
        </li>
        <li>
          <strong>Do not stop wearing sunscreen.</strong> The skin-cancer
          evidence is not close. Change product, not habit.
        </li>
        <li>
          <strong>The baby range is fine.</strong> Polyquaternium is not a
          microplastic, whatever your app says.
        </li>
        <li>
          <strong>Keep using the pimple patches.</strong> You remove them
          intact. This is not the thing to fix.
        </li>
      </ul>

      <p>
        See the{' '}
        <Link href="/blog/drugstore-skincare-microplastics-audited" className="text-forest underline">full 290-formula audit</Link>,
        the{' '}
        <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe result</Link>,
        and{' '}
        <Link href="/blog/best-sunscreens-without-microplastics-brands-ranked" className="text-forest underline">sunscreens without microplastics</Link>{' '}
        for the swap. For the wider category,{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">the July makeup audit</Link>, and the third brand in the cluster, <Link href="/blog/does-neutrogena-have-microplastics" className="text-forest underline">Neutrogena</Link>.
      </p>

      <p>
        A second dataset: the{' '}
        <Link href="/brands/cetaphil" className="text-forest underline">
          Cetaphil brand page
        </Link>{' '}
        shows what Open Beauty Facts lists on Cetaphil ingredient labels, a
        crowdsourced and Europe-weighted sample.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does Cetaphil have microplastics?',
            answer: 'Five of the 79 Cetaphil formulas that publish an ingredient list contain a solid synthetic polymer particle: the Daily Facial Moisturizer SPF 15 and DermaControl Oil Absorbing Moisturizer SPF 30 (both polymethyl methacrylate), the Healthy Radiance Whipped Day Cream SPF 30 (acrylates copolymer), the Hydrating Eye Gel-Cream and the Fast Rescue Pimple Patch. That is about 6%. The classic Gentle Skin Cleanser and Moisturizing Lotion are clean.',
          },
          {
            question: 'Is Cetaphil Gentle Skin Cleanser free of microplastics?',
            answer: 'Yes. The Gentle Skin Cleanser contains no solid synthetic polymer particles according to the ingredient list Galderma publishes on cetaphil.com. The same applies to the Cetaphil Moisturizing Lotion and the Daily Facial Cleanser. These are the products the brand is best known for and none of them are affected.',
          },
          {
            question: 'Does Cetaphil Baby have microplastics?',
            answer: 'No solid synthetic polymer particles. Several Cetaphil Baby products contain polyquaternium, which many ingredient-checking apps incorrectly flag as a microplastic. Polyquaternium is a water-soluble cationic conditioning polymer, dissolved rather than suspended as solid particles, so it falls outside the EU definition of synthetic polymer microparticles.',
          },
          {
            question: 'Is Cetaphil or CeraVe better for avoiding microplastics?',
            answer: 'They are effectively level — Cetaphil at 5 of 79 published formulas and CeraVe at 4 of 71, both around 6%. The practical difference is that Cetaphil’s hits are concentrated in SPF moisturisers, so changing one product category addresses nearly all of them, while CeraVe’s are spread across four unrelated products with no single fix.',
          },
          {
            question: 'Should I stop using Cetaphil sunscreen?',
            answer: 'Not without replacing it. The SPF moisturisers contain polymethyl methacrylate or acrylates copolymer, which provide the soft-focus, non-greasy finish. But daily sun protection has overwhelming evidence behind it and the harm case for these specific ingredients through intact skin is very weak. If you want to avoid them, switch to a mineral sunscreen rather than skipping SPF.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'MicroPlastics Research Desk', year: 2026, title: 'First-party INCI audit of 79 published Cetaphil formulas (product sitemap walked, ingredient lists extracted from cetaphil.com, August 2026)', publisher: 'MicroPlastics' },
          { authors: 'Galderma (Cetaphil)', year: 2026, title: 'Cetaphil product ingredient listings', publisher: 'cetaphil.com', url: 'https://www.cetaphil.com/' },
          { authors: 'European Commission', year: 2023, title: 'Commission Regulation (EU) 2023/2055 restricting synthetic polymer microparticles (solid-particle definition; REACH Annex XVII entry 78)', publisher: 'Official Journal of the European Union', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />
    </BlogPostLayout>
  )
}
